<template>
  <div>
    <v-container
      grid-list-xl
      fluid
    >
      <v-layout
        row
        wrap
        class="mt-3"
      >
        <v-toolbar
          flat
          color="transparent"
          class="mb-4 mt-4"
          height="20px"
        >
          <v-btn
            icon
            class="text--secondary"
            @click="goBack"
          >
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <span class="text-capitalize subheading text--secondary">
            {{ $t('Settings') }}
          </span>
        </v-toolbar>
        <v-flex
          xs12
        >
          <v-layout
            row
            wrap
          >
            <v-flex
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Requested Delivery') }}
              </h3>
              <p class="caption">
                {{ $t('Configuration for a requested delivery date that customer can select at checkout.') }}
              </p>
              <p class="caption">
                {{ $t('With the requested delivery enabled, the system will let customer choose when he would want his order to arrive.') }}
              </p>
              <p class="caption mb-0">
                {{ $t('With the delivery enforced (when it is enabled), the store admin will be hinted about requested delivery and that the shipping time approaches.') }}
              </p>
              <p class="caption mb-0">
                {{ $t('The time interval is meant to specify at what minutes the delivery can be requested, like at every 15 or every 30.') }}
              </p>
              <p class="caption mb-0">
                {{ $t('The delivery window specifies how soon the admin should be alerted about the requested delivery date approaching.') }}
              </p>
              <p class="caption mb-0">
                {{ $t('The blackout settings are going to specify what dates, days or hours should not be available for selection by customer.') }}
              </p>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <div v-if="checkoutSettings">
                  <v-switch
                    v-model="checkoutSettings.requesting_delivery_allowed"
                    :label="$t('Enable Requested Delivery')"
                  />

                  <v-switch
                    v-model="checkoutSettings.enable_delivery_enforcement"
                    :label="$t('Enable Delivery Enforcement')"
                    :disabled="!checkoutSettings.requesting_delivery_allowed"
                  />

                  <v-switch
                    v-model="checkoutSettings.show_delivery_time"
                    :label="$t('Show Delivery Time')"
                    :disabled="!checkoutSettings.requesting_delivery_allowed"
                  />

                  <v-text-field
                    v-model="checkoutSettings.delivery_interval"
                    :label="$t('Delivery Time Interval')"
                    :disabled="!checkoutSettings.requesting_delivery_allowed"
                    mask="##"
                    return-masked-value
                  />

                  <v-text-field
                    v-model.number="orderSettings.requested_delivery_window"
                    :label="$t('Delivery Shipping Window')"
                    :disabled="!checkoutSettings.enable_delivery_enforcement || !checkoutSettings.requesting_delivery_allowed"
                    mask="##"
                    return-masked-value
                  />

                  <v-combobox
                    v-model="checkoutSettings.delivery_blackouts.weekdays"
                    :label="$t('Delivery Blackouts Weekdays')"
                    :items="weekdays"
                    :disabled="!checkoutSettings.requesting_delivery_allowed"
                    :return-object="false"
                    multiple
                    chips
                    deletable-chips
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        :key="data.item"
                        :selected="data.selected"
                        :disabled="data.disabled"
                        class="v-chip--select-multi"
                        close
                        @click.stop="data.parent.selectedIndex = data.index"
                        @input="data.parent.selectItem(data.item)"
                      >
                        {{ $t(upperCase(data.item)) }}
                      </v-chip>
                    </template>
                  </v-combobox>

                  <v-layout
                    row
                    wrap>
                    <v-flex xs12>
                      <v-menu
                        ref="menu"
                        v-model="hoursMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="checkoutSettings.delivery_blackouts.hours"
                        :disabled="!checkoutSettings.requesting_delivery_allowed"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-combobox
                            v-model="checkoutSettings.delivery_blackouts.hours"
                            :label="$t('Delivery Blackouts Hours')"
                            :disabled="!checkoutSettings.requesting_delivery_allowed"
                            :loading="timezoneLoading"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            v-on="on"
                          />
                        </template>
                        <v-card>
                          <v-card-text>
                            <v-text-field
                              v-mask="timeRangeMask"
                              v-model="hoursValue"
                              return-masked-value
                              label="##:##-##:##"
                            />
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer/>
                            <v-btn
                              flat
                              color="primary"
                              @click="addHours">{{ $t('Add') }}</v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-menu>
                    </v-flex>
                  </v-layout>

                  <v-layout
                    row
                    wrap>
                    <v-flex xs12>
                      <v-menu
                        ref="menu"
                        v-model="menu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="checkoutSettings.delivery_blackouts.dates"
                        :disabled="!checkoutSettings.requesting_delivery_allowed"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-combobox
                            v-model="checkoutSettings.delivery_blackouts.dates"
                            :label="$t('Delivery Blackouts Days')"
                            :disabled="!checkoutSettings.requesting_delivery_allowed"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            v-on="on"
                          />
                        </template>
                        <v-date-picker
                          v-model="checkoutSettings.delivery_blackouts.dates"
                          multiple
                          no-title
                          scrollable>
                          <v-spacer/>
                          <v-btn
                            flat
                            color="primary"
                            @click="menu = false">Cancel</v-btn>
                          <v-btn
                            flat
                            color="primary"
                            @click="$refs.menu.save(checkoutSettings.delivery_blackouts.dates)">OK</v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-flex>
                  </v-layout>
                </div>
                <v-layout
                  v-else
                  justify-center
                  class="py-5">
                  <v-progress-circular
                    color="primary"
                    indeterminate
                    size="50"
                  />
                </v-layout>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <v-layout
      row
      wrap
      color="transparent"
    >
      <v-flex
        xs12
        ma-3
      >
        <v-divider mb-3/>
        <v-btn
          large
          class="primary right mt-3"
          @click="save()"
        >
          {{ $t('Save') }}
        </v-btn>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import LoadingState from '@/components/LoadingState'
import ImageUpload from '@/components/ImageUpload'
import LanguagesList from '@/modules/Settings/components/LanguagesList'
import { mapGetters } from 'vuex'
import { getTimeZoneOffset } from '@/util/timeZoneOffset'

export default {
  name: 'SettingsCheckout',

  components: {
    LanguagesList,
    ImageUpload,
    LoadingState
  },

  data () {
    return {
      progress: false,
      menu: false,
      hoursMenu: false,
      hoursValue: null,
      checkoutSettings: null,
      orderSettings: {
        requested_delivery_window: null
      }
    }
  },

  computed: {
    ...mapGetters(['timezone', 'timezoneLoading']),
    weekdays () {
      return [
        { text: this.$t('Monday'), value: 'monday' },
        { text: this.$t('Tuesday'), value: 'tuesday' },
        { text: this.$t('Wednesday'), value: 'wednesday' },
        { text: this.$t('Thursday'), value: 'thursday' },
        { text: this.$t('Friday'), value: 'friday' },
        { text: this.$t('Saturday'), value: 'saturday' },
        { text: this.$t('Sunday'), value: 'sunday' }
      ]
    },
    timezoneOffset () {
      return getTimeZoneOffset(new Date(), this.timezone)
    }
  },

  watch: {
    hoursMenu: function () {
      this.hoursValue = null
    },
    timezoneLoading: async function () {
      await this.initCheckoutSettings()
    }
  },

  async mounted () {
    this.orderSettings = await this.$http.make('settings').find('general/order')
    if (!this.timezoneLoading) {
      await this.initCheckoutSettings()
    }
  },

  methods: {
    async initCheckoutSettings () {
      const checkoutSettings = await this.$http.make('settings').find('general/checkout')
      const hours = checkoutSettings.delivery_blackouts.hours
      checkoutSettings.delivery_blackouts.hours = await this.shiftBlackoutHours(hours, this.timezoneOffset)
      this.checkoutSettings = { ...checkoutSettings }
    },
    addHours () {
      if (this.checkoutSettings.delivery_blackouts.hours === null) {
        this.checkoutSettings.delivery_blackouts.hours = []
      }

      this.checkoutSettings.delivery_blackouts.hours.push(this.hoursValue)
      this.hoursValue = null
      this.hoursMenu = false
    },

    async save () {
      const deliveryBlackouts = { ...this.checkoutSettings.delivery_blackouts }

      deliveryBlackouts.hours = await this.shiftBlackoutHours(deliveryBlackouts.hours || [], -this.timezoneOffset)

      const checkout = this.$http.make('settings', { data: { ...this.checkoutSettings, delivery_blackouts: deliveryBlackouts } })
      checkout.code_url = 'general/checkout'

      const deliveryWindow = this.$http.make('settings', { data: this.orderSettings })
      deliveryWindow.code_url = 'general/order'

      try {
        await checkout.save()
        await deliveryWindow.save()
        this.$toast(this.$t('Checkout settings saved'))
      } catch (e) {
        this.$toast.error(this.$t(e.message))
      }
    },

    shiftTime (time, ms) {
      const [hours, minutes] = time.split(':')
      const date = new Date()
      date.setHours(hours)
      date.setMinutes(minutes)
      const diff = date.getTime() - ms
      const shiftedDate = new Date(diff)
      return `${shiftedDate.getHours().toString().padStart(2, '0')}:${shiftedDate.getMinutes().toString().padStart(2, '0')}`
    },

    async shiftBlackoutHours (items, ms) {
      return items.map(time => {
        const [from, to] = time.split('-')
        const timeFrom = this.shiftTime(from, ms)
        const timeTo = this.shiftTime(to, ms)
        return `${timeFrom}-${timeTo}`
      })
    },

    upperCase (string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },

    goBack () {
      this.$router.push({ name: 'settings' })
    },

    /**
     * Generate a time mask based on input value (23:59)
     * @param {string} value
     */
    timeMask (value) {
      const hours = [
        /[0-2]/,
        value.charAt(0) === '2' ? /[0-3]/ : /[0-9]/
      ]
      const minutes = [/[0-5]/, /[0-9]/]
      return value.length > 2
        ? [...hours, ':', ...minutes]
        : hours
    },

    /**
     * Generate a time range mask based on input value (00:00-23:59)
     * @param {string} value
     */
    timeRangeMask (value) {
      const numbers = value.replace(/[^0-9]/g, '')
      if (numbers.length > 4) {
        return [...this.timeMask(numbers.substring(0, 4)), '-', ...this.timeMask(numbers.substring(4))]
      }
      return [...this.timeMask(numbers)]
    }
  }
}
</script>
